import type { SimpleEditorCollaborationConfig } from "src/tiptap/components/tiptap-templates/simple/simple-editor";
import type { JSONContent } from "@tiptap/core";
import { useEffect, useMemo, useState } from "react";
import { getTiptapCloudDocument } from "src/services/backend";
import {
  getTiptapCollaborationTokenForOrg,
  TIPTAP_COLLABORATION_TOKEN_REFRESH_BUFFER_MS,
} from "src/services/tiptap-collaboration-token";

const TIPTAP_COLLABORATION_TOKEN_REFRESH_RETRY_MS = 60 * 1000;

const getOrgScopedTiptapDocumentName = (orgId: string, documentId: string) => {
  const trimmedDocumentId = documentId.trim();
  const orgDocumentPrefix = `${orgId}/`;

  if (trimmedDocumentId.startsWith(orgDocumentPrefix)) {
    return trimmedDocumentId;
  }

  return `${orgDocumentPrefix}${trimmedDocumentId.replace(/^\/+/, "")}`;
};

const getTiptapLoadErrorMessage = (error: unknown) => {
  const response = (error as { response?: { data?: { message?: unknown } } })
    ?.response;
  const message = response?.data?.message;

  return typeof message === "string" && message.trim()
    ? message
    : "Unable to load Tiptap document.";
};

export const useTiptapCollaborationDocument = ({
  collaborationEnabled = true,
  documentName,
  orgId,
}: {
  collaborationEnabled?: boolean;
  documentName?: string;
  orgId?: string;
}) => {
  const orgScopedDocumentName = useMemo(() => {
    if (!orgId || !documentName) {
      return null;
    }

    return getOrgScopedTiptapDocumentName(orgId, documentName);
  }, [documentName, orgId]);
  const [documentContent, setDocumentContent] = useState<JSONContent | null>(
    null
  );
  const [collaborationConfig, setCollaborationConfig] =
    useState<SimpleEditorCollaborationConfig | null>(null);
  const [collaborationTokenExpiresAtMs, setCollaborationTokenExpiresAtMs] =
    useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadedDocumentName, setLoadedDocumentName] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (!orgId || !documentName || !orgScopedDocumentName) {
      setDocumentContent(null);
      setCollaborationConfig(null);
      setCollaborationTokenExpiresAtMs(null);
      setLoadError(null);
      setLoadedDocumentName(null);
      return;
    }

    let isMounted = true;
    setLoadError(null);
    if (!collaborationEnabled) {
      setCollaborationConfig(null);
      setCollaborationTokenExpiresAtMs(null);
    }

    Promise.all([
      getTiptapCloudDocument({
        orgId,
        documentName,
      }),
      collaborationEnabled
        ? getTiptapCollaborationTokenForOrg(orgId)
        : Promise.resolve(null),
    ])
      .then(([documentResponse, collaborationToken]) => {
        if (!isMounted) {
          return;
        }

        setDocumentContent(documentResponse.data.content ?? null);
        setLoadedDocumentName(orgScopedDocumentName);
        setCollaborationConfig(
          collaborationToken
            ? {
                appId: collaborationToken.appId,
                documentName: orgScopedDocumentName,
                token: collaborationToken.token,
                userId: collaborationToken.userId,
              }
            : null
        );
        setCollaborationTokenExpiresAtMs(
          collaborationToken?.expiresAtMs ?? null
        );
      })
      .catch((error: unknown) => {
        if (!isMounted) {
          return;
        }

        setDocumentContent(null);
        setCollaborationConfig(null);
        setCollaborationTokenExpiresAtMs(null);
        setLoadedDocumentName(null);
        setLoadError(getTiptapLoadErrorMessage(error));
      });

    return () => {
      isMounted = false;
    };
  }, [collaborationEnabled, documentName, orgId, orgScopedDocumentName]);

  useEffect(() => {
    if (!orgId || !collaborationConfig || !collaborationTokenExpiresAtMs) {
      return;
    }

    let isMounted = true;
    let refreshTimeoutId: number | undefined;

    const scheduleRefresh = (delayMs: number) => {
      refreshTimeoutId = window.setTimeout(() => {
        getTiptapCollaborationTokenForOrg(orgId, { forceRefresh: true })
          .then((collaborationToken) => {
            if (!isMounted) {
              return;
            }

            setCollaborationConfig((currentConfig) => {
              if (!currentConfig) {
                return currentConfig;
              }

              return {
                ...currentConfig,
                appId: collaborationToken.appId,
                token: collaborationToken.token,
                userId: collaborationToken.userId,
              };
            });
            setCollaborationTokenExpiresAtMs(collaborationToken.expiresAtMs);
            setLoadError(null);
          })
          .catch((error: unknown) => {
            if (!isMounted) {
              return;
            }

            setLoadError(getTiptapLoadErrorMessage(error));
            scheduleRefresh(TIPTAP_COLLABORATION_TOKEN_REFRESH_RETRY_MS);
          });
      }, delayMs);
    };

    // The Tiptap websocket may need to reconnect after laptop sleep, network
    // changes, or tab suspension. If that reconnect happens after the JWT
    // expires, Tiptap Cloud rejects the old token, so refresh before expiry and
    // let SimpleEditor rebuild the provider with fresh credentials.
    scheduleRefresh(
      Math.max(
        collaborationTokenExpiresAtMs -
          Date.now() -
          TIPTAP_COLLABORATION_TOKEN_REFRESH_BUFFER_MS,
        0
      )
    );

    return () => {
      isMounted = false;
      window.clearTimeout(refreshTimeoutId);
    };
  }, [collaborationConfig?.token, collaborationTokenExpiresAtMs, orgId]);

  const activeCollaborationConfig =
    collaborationEnabled &&
    collaborationConfig?.documentName === orgScopedDocumentName
      ? collaborationConfig
      : null;
  const activeDocumentContent =
    loadedDocumentName === orgScopedDocumentName ? documentContent : null;
  const isDocumentLoadPending = Boolean(
    orgScopedDocumentName &&
      !loadError &&
      loadedDocumentName !== orgScopedDocumentName
  );

  return {
    collaborationConfig: activeCollaborationConfig,
    documentContent: activeDocumentContent,
    isLoading: isDocumentLoadPending,
    loadError,
    orgScopedDocumentName,
  };
};
